import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { NgClass } from '@angular/common';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ImageCroppedEvent, ImageCropperComponent } from 'ngx-image-cropper';
import { DirectUpload } from 'activestorage/src/direct_upload';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { Tenant } from '@parashift/shared/models';
import { CurrentTenantRegister, EnvService, ErrorFormatter, Logger, NotificationService, SessionService, TenantService } from '@parashift/shared/services';
import { Blob, UploadedFile } from '@parashift/shared/components';

@Component({
  selector: 'app-logo-upload-resize',
  templateUrl: './logo-upload-resize.component.html',
  styleUrls: ['./logo-upload-resize.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, FaIconComponent, TooltipModule, ImageCropperComponent]
})
export class LogoUploadResizeComponent implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private sanitizer = inject(DomSanitizer);
  private tenantService = inject(TenantService);
  private sessionService = inject(SessionService);
  private currentTenantRegister = inject(CurrentTenantRegister);
  private errorFormatter = inject(ErrorFormatter);
  private notificationService = inject(NotificationService);
  private logger = inject(Logger);

  imageChangedEvent: Event;
  croppedImageUrl: SafeUrl;
  base64Image: string;
  fileName: string;
  showCropper = false;
  input: HTMLInputElement;
  allowedMimeTypes = ['image/jpeg', 'image/png', 'image/gif'];
  fileIsOver = false;

  private environment = inject(EnvService);
  private activeStorageHost = this.environment.endpoints.id;
  private activeStoragePath = '/v1/direct_uploads';
  private tenant: Tenant;

  ngOnInit() {
    this.tenant = this.currentTenantRegister.tenant;
  }

  fileChangeEvent(event: Event) {
    this.showCropper = true;
    this.input = (event.target as HTMLInputElement);
    const files = Array.from(this.input.files as FileList);
    this.fileName = files?.[0].name;
    this.imageChangedEvent = event;
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.fileIsOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.fileIsOver = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);

    if (files?.[0] && this.checkMimeTypes(files[0].type)) {
      this.showCropper = true;
      this.fileName = files[0].name;
      this.imageChangedEvent = { target: { files: event.dataTransfer.files }} as unknown as Event;
    }
    this.fileIsOver = false;
  }

  imageCropped(event: ImageCroppedEvent) {
    this.croppedImageUrl = this.sanitizer.bypassSecurityTrustUrl(event.base64);
    this.base64Image = event.base64;

  }

  loadImageFailed() {
    this.notificationService.error({
      title: `:@@components.logo_upload_resize.loading_image_error_title:Loading field error`,
      description: `:@@components.logo_upload_resize.loading_image_error_description:Loading image into image cropper failed`
    });
  }

  cancelCropping() {
    this.resetLoadedImage();
  }

  uploadImage() {
    const file = this.base64ToFile(this.base64Image, this.fileName);
    file && this.performUpload(file);
  }

  private resetLoadedImage() {
    if (this.input) {
      this.input.value = '';
    }

    this.showCropper = false;
    this.croppedImageUrl = undefined;
    this.base64Image= undefined;
    this.imageChangedEvent = undefined;
    this.fileName = undefined;
    this.cdr.detectChanges();
  }

  private base64ToFile(dataurl: string, filename: string): File {
    const arr = dataurl.split(',');
    const mimeType = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const uint8Array = new Uint8Array(n);

    while (n--) {
      uint8Array[n] = bstr.charCodeAt(n);
    }

    return new File([uint8Array], filename, { type: mimeType });
  }

  private performUpload(file: File) {
    if (this.checkMimeTypes(file.type)) {
      const upload = new DirectUpload(
        file, this.activeStorageHost + this.activeStoragePath, {
          directUploadWillCreateBlobWithXHR: (xhr: XMLHttpRequest) => {
            xhr.setRequestHeader('Authorization', `Bearer ${this.sessionService.session_token}`);
            xhr.setRequestHeader('Content-Type', file.type);
          }
        }
      );

      upload.create((error: string, blob: Blob) => {
        if (error) {
          this.notificationService.error({
            title: $localize `:@@components.logo_upload_resize.upload_error:Upload error`,
            description: $localize `:@@components.logo_upload_resize.upload_error_try_later:There was an error with uploading the file. Please contact customer support or try again later.`
          });
        } else {
          this.addLogoToTenant({ blob, fileName: file.name });
        }
      });
    }
  }

  private checkMimeTypes(fileType): boolean {
    if (fileType && this.allowedMimeTypes.find(type => fileType === type)) {
      return true;
    }

    this.notificationService.error({
      title: `:@@components.logo_upload_resize.file_format_error_title:File format error`,
      description: `:@@components.logo_upload_resize.file_format_error_description:This file format is not allowed. Please try another file`
    });
    return false;
  }

  private addLogoToTenant(file: Partial<UploadedFile>) {
    this.tenantService.addLogoFromDirectUpload(
      file.blob as Blob,
      Number(this.tenant.id),
    ).subscribe({
      next: () => {
        this.resetLoadedImage();
        this.reloadTenant();
        this.notificationService.success({
          description: $localize `:@@components.logo_upload_resize.upload_success_info:The file has been successfully uploaded.`,
          title: $localize `:@@components.logo_upload_resize.upload_success:Upload success`
        });
      },
      error: error => {
        const formattedError = this.errorFormatter.format(error);
        this.notificationService.error(formattedError);
        this.logger.error('PATCH TENANT AFTER UPLOAD ERROR', file.blob, error);
      }
    });
  }

  private reloadTenant() {
    this.currentTenantRegister.loadTenant(this.tenant.id).subscribe();
  }
}
